import {
  getResponsibilityActivatePreview,
  getResponsibilityReactivatePreview,
  getRoleActivatePreview,
  getRoleReactivatePreview,
} from "@/api";

/**
 * ER-11818 — resolve the employee count (N) returned by the activate/reactivate preview
 * endpoints. The preview contracts are owned by backend tickets ER-11817 (Responsibility)
 * and ER-11822 (Role), whose response shapes are not yet finalised. To stay robust to the
 * common serializer variants, N is read from the first populated numeric field in a fixed
 * order: `count`, `employees_count`, `assigned_employees_count`.
 *
 * @param {*} response axios response (or its `data` payload)
 * @returns {number} N, coerced to a non-negative integer (0 when absent/unparseable).
 */
export function resolveAssignedEmployeeCount(response) {
  const data = response?.data ?? response;
  if (data == null) return 0;

  const candidates = [data.count, data.employees_count, data.assigned_employees_count];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return Math.floor(parsed);
  }
  return 0;
}

/**
 * Resolve the preview request for an object type + verb.
 *
 * @param {"role"|"responsibility"} objectType
 * @param {"activate"|"reactivate"} kind
 */
function requestPreview(objectType, kind, accountId, objectId) {
  if (objectType === "role") {
    return kind === "activate"
      ? getRoleActivatePreview(accountId, objectId)
      : getRoleReactivatePreview(accountId, objectId);
  }
  return kind === "activate"
    ? getResponsibilityActivatePreview(accountId, objectId)
    : getResponsibilityReactivatePreview(accountId, objectId);
}

/**
 * Fetch N for an activate/reactivate trigger.
 *
 * @param {"role"|"responsibility"} objectType
 * @param {"activate"|"reactivate"} kind
 * @returns {Promise<number|null>} N, or null when it could not be determined (missing id
 *   or preview failure) so callers fall back to showing the confirmation dialog.
 */
export async function fetchActivationCount(objectType, kind, accountId, objectId) {
  if (!accountId || !objectId) return null;
  try {
    return resolveAssignedEmployeeCount(
      await requestPreview(objectType, kind, accountId, objectId)
    );
  } catch (error) {
    console.error(`Error fetching ${objectType} ${kind} preview:`, error);
    return null;
  }
}

/** Fetch N for a Responsibility activate/reactivate trigger. */
export function fetchResponsibilityActivationCount(kind, accountId, responsibilityId) {
  return fetchActivationCount("responsibility", kind, accountId, responsibilityId);
}

/** Fetch N for a Role activate/reactivate trigger. */
export function fetchRoleActivationCount(kind, accountId, roleId) {
  return fetchActivationCount("role", kind, accountId, roleId);
}

export default resolveAssignedEmployeeCount;
